import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { UserPlus, ShieldCheck, Phone, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = { ink: "#0B1E3B", red: "#E01A2E", green: "#1E9E63", slate: "#5A6472", line: "#E3E8F0", bg: "#F5F7FA" };

export default function FleetOnboard() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [name, setName] = useState("");
  const [cell, setCell] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setData((await axios.get(`${API}/public/fleet/${token}`)).data); }
    catch (e) { setErr(true); }
  };
  useEffect(() => { load(); }, [token]);

  const add = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) return toast.error("Enter the driver's full name");
    if (cell.replace(/\D/g, "").length < 9) return toast.error("Enter a valid cell number");
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/public/fleet/${token}`, { name, cell });
      toast.success(r.updated ? `${r.full_name} updated` : `${r.full_name} added & covered`);
      setName(""); setCell(""); load();
    } catch (e2) { toast.error("Couldn't add driver", { description: e2.response?.data?.detail || e2.message }); }
    finally { setBusy(false); }
  };

  if (err) return <Center>This onboarding link is not valid. Please check with ResQ1.</Center>;
  if (!data) return <Center><Loader2 className="spin" size={22} /> Loading…</Center>;

  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: "Arial,sans-serif" }} data-testid="fleet-onboard">
      <Toaster position="top-center" richColors />
      <div style={{ background: C.ink, color: "#fff", padding: "22px 20px" }}>
        <div style={{ fontSize: 12, letterSpacing: 2, color: "#9FB3D1" }}>RESQ1 · DRIVER SAFETY</div>
        <div style={{ fontSize: 24, fontWeight: 800, marginTop: 4 }} data-testid="fleet-partner-name">{data.partner} — add your drivers</div>
        <div style={{ fontSize: 13, color: "#C7D3E6", marginTop: 6, lineHeight: 1.5 }}>
          Add each driver by name and cell number. They're covered for armed-response &amp; ambulance dispatch the moment you save them — no email needed.
        </div>
        {data.control_room && (
          <div style={{ marginTop: 12, display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.1)", padding: "8px 14px", borderRadius: 10, fontSize: 14, fontWeight: 700 }}>
            <Phone size={15} /> Control room: {data.control_room}
          </div>
        )}
      </div>

      <div style={{ maxWidth: 560, margin: "0 auto", padding: 18 }}>
        <form onSubmit={add} style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 14, padding: 18 }}>
          <div style={{ fontWeight: 700, color: C.ink, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}><UserPlus size={18} color={C.red} /> Add a driver</div>
          <label style={lbl}>Driver's full name</label>
          <input data-testid="fleet-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sipho Khumalo" style={inp} />
          <label style={lbl}>Driver's cell number</label>
          <input data-testid="fleet-cell" value={cell} onChange={(e) => setCell(e.target.value)} placeholder="e.g. 082 123 4567" inputMode="tel" style={inp} />
          <button type="submit" disabled={busy} data-testid="fleet-add-btn" style={{ ...btn, opacity: busy ? 0.6 : 1 }}>
            {busy ? <Loader2 className="spin" size={17} /> : <UserPlus size={17} />} Add driver &amp; activate cover
          </button>
        </form>

        <div style={{ marginTop: 16, background: "#fff", border: `1px solid ${C.line}`, borderRadius: 14, padding: 18 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ fontWeight: 700, color: C.ink }}>Drivers on cover</div>
            <div data-testid="fleet-count" style={{ fontSize: 13, fontWeight: 700, color: C.green, display: "flex", alignItems: "center", gap: 6 }}><ShieldCheck size={16} /> {data.count}</div>
          </div>
          {data.drivers.length === 0 ? (
            <div style={{ color: C.slate, fontSize: 13 }}>No drivers added yet.</div>
          ) : data.drivers.map((d, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: i < data.drivers.length - 1 ? `1px solid ${C.line}` : 0 }}>
              <CheckCircle2 size={16} color={C.green} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: C.ink, fontSize: 14 }}>{d.full_name}</div>
                <div style={{ color: C.slate, fontSize: 12 }}>{d.cell}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 16, fontSize: 12, color: C.slate, lineHeight: 1.6, textAlign: "center" }}>
          Tip: install ResQ1 on this tablet — open <b>resq1.co.za/app</b> and tap <b>Add to Home Screen</b>.
        </div>
      </div>
      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

const lbl = { display: "block", fontSize: 12, fontWeight: 600, color: "#5A6472", margin: "10px 0 5px" };
const inp = { width: "100%", padding: 12, border: "1px solid #D3DEEC", borderRadius: 10, fontSize: 15, boxSizing: "border-box" };
const btn = { marginTop: 16, width: "100%", padding: 14, background: "#E01A2E", color: "#fff", border: 0, borderRadius: 11, fontSize: 15, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 };

function Center({ children }) {
  return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: "#5A6472", fontFamily: "Arial,sans-serif", padding: 24, textAlign: "center" }}>{children}</div>;
}
